import {
  COSTS_UNAVAILABLE,
  annualRate,
  blockAvailable,
  blockReason,
  money,
  percent,
  shareBps,
  signedMoney,
} from "./format";

const TONE_CLASS = { good: "green", bad: "pressure", neutral: "neutral", locked: "neutral" };

const Kpi = ({ tile }) => <article
  className={`director-module1__metric director-module1__metric--${TONE_CLASS[tile.tone] || "neutral"} director-dashboard-kpi${tile.locked ? " director-dashboard-kpi--locked" : ""}`}
>
  <span className="director-dashboard-kpi__label">
    <span className="director-dashboard-kpi__name">{tile.label}</span>
    {tile.provisional && <small className="director-dashboard-kpi__flag">taxa provisória</small>}
  </span>
  <strong className="director-dashboard-kpi__value" data-tone={tile.tone}>{tile.value}</strong>
  {(tile.pills || []).filter(Boolean).map(([text, tone]) => <span className="director-dashboard-pill" data-tone={tone} key={text}>
    {text}
  </span>)}
  {tile.note && <small className="director-dashboard-kpi__note">{tile.note}</small>}
  {tile.action}
</article>;

const irrNote = (totals, rateText) => {
  if (totals.irrAmbiguous) return "O fluxo muda de sinal várias vezes e a TIR fica ambígua: use o VPL";
  if (totals.irrBps === null || totals.irrBps === undefined) return "Não se aplica: o fluxo não troca de sinal";
  return `Contra ${rateText} de custo de capital`;
};

const KpiStrip = ({ data, onOpenPremises }) => {
  const totals = data.totals || {};
  const obras = data.obras || [];
  const rateText = annualRate(data.rate?.bps);
  const provisional = data.rate?.provisional === true;
  const liquidity = blockAvailable(data, "liquidity");
  const returns = blockAvailable(data, "returns");
  const margin = blockAvailable(data, "margin");
  const costsUnavailable = blockReason(data, "margin") === "costs_unavailable";

  const toCheck = obras.filter((obra) => obra.situation === "check_cost").map((obra) => obra.name);
  const checkPill = toCheck.length ? [`Inclui ${toCheck.join(", ")}: custo a conferir`, "warn"] : null;

  const gapMonths = totals.gap?.months || 0;
  const worstGap = totals.gap?.worstCents ?? 0;
  const liquidityTiles = liquidity ? [
    {
      key: "exposure",
      label: "Exposição máxima de caixa",
      value: totals.exposureCents > 0 ? money(totals.exposureCents) : "Nenhuma",
      tone: "neutral",
      note: totals.exposureCents > 0
        ? "Soma do pior momento de cada obra: dinheiro próprio que as obras exigem"
        : "Nenhuma obra ficou com acumulado negativo",
    },
    {
      key: "gap",
      label: "Desencaixe nos próximos 12 meses",
      value: gapMonths ? `${gapMonths} ${gapMonths === 1 ? "mês" : "meses"}` : "Nenhum",
      // Meses no vermelho sem aporte no pior momento não contradizem "Caixa coberto": tom neutro.
      tone: gapMonths ? (worstGap < 0 ? "bad" : "neutral") : "good",
      note: worstGap < 0
        ? `Somando a falta de cada obra: ${money(-worstGap)} de aporte no pior momento`
        : "Nenhuma obra fica com o acumulado negativo",
      pills: [worstGap < 0 ? ["Precisa de aporte", "bad"] : ["Caixa coberto", "good"]],
    },
  ] : [];

  const goalObras = obras.filter((obra) => obra.goal && !obra.goal.reached && obra.goal.cents > 0).length;
  const goalTile = (pills) => returns && {
    key: "goal",
    label: "Meta de vendas",
    provisional,
    value: totals.goalCents > 0 ? money(totals.goalCents) : "Atingida",
    tone: totals.goalCents > 0 ? "neutral" : "good",
    note: totals.goalCents > 0
      ? `Quanto ${goalObras === 1 ? "a obra precisa" : `${goalObras} obras precisam`} vender para empatar a ${rateText}`
      : "O que já foi vendido paga as obras a essa taxa",
    pills,
  };

  const overrunKnown = Number.isSafeInteger(totals.overrunCents);
  const overrunShare = shareBps(totals.overrunCents, totals.budgetIndexedCents);
  let overrunTile = null;
  if (margin) {
    overrunTile = {
      key: "overrun",
      label: "Estouro de custo",
      value: signedMoney(totals.overrunCents),
      // Sem estouro calculado, "—" não pode sair verde como se estivesse tudo certo.
      tone: overrunKnown ? (totals.overrunCents > 0 ? "bad" : "good") : "neutral",
      note: `Tendência do Ampli Obras contra o orçado indexado${overrunKnown && overrunShare !== null ? ` (${percent(overrunShare)})` : ""}`,
    };
  } else if (costsUnavailable) {
    overrunTile = {
      key: "overrun",
      label: "Estouro de custo",
      value: "Indisponível",
      tone: "neutral",
      note: COSTS_UNAVAILABLE,
    };
  }

  let tiles;
  let coverage = null;
  if (data.readingMode !== "with_premises") {
    tiles = [
      goalTile([checkPill]),
      ...liquidityTiles,
      overrunTile,
      returns && {
        key: "locked",
        label: "VPL e TIR",
        value: "Complete o estoque",
        tone: "locked",
        locked: true,
        note: "Informe o estoque a vender de cada obra para ver VPL, TIR e margem completa.",
        action: <button className="director-link-button director-link-button--secondary director-dashboard-kpi__action" type="button" onClick={onOpenPremises}>
          Complementar dados
        </button>,
      },
    ];
  } else {
    const missing = obras.filter((obra) => !obra.hasStock).map((obra) => obra.name);
    const partialPill = missing.length ? [`Sem estoque: ${missing.join(", ")}`, "warn"] : null;
    if (returns) {
      coverage = <p className="director-dashboard-kpis__coverage">
        <span className="director-dashboard-kpis__coverage-text">Lucro, VPL e TIR somam </span>
        <strong className="director-dashboard-kpis__coverage-count">{`${totals.obrasWithStock} de ${totals.obrasCount} obras`}</strong>
        <span className="director-dashboard-kpis__coverage-text"> com estoque informado.</span>
      </p>;
    }
    tiles = [
      returns && {
        key: "total",
        label: "Lucro projetado",
        value: money(totals.totalCents),
        tone: totals.totalCents < 0 ? "bad" : "good",
        note: "Tudo o que entra menos tudo o que sai, do início ao fim",
        pills: [checkPill],
      },
      returns && {
        key: "npv",
        label: `VPL a ${rateText}`,
        provisional,
        value: money(totals.npvCents),
        tone: totals.npvCents < 0 ? "bad" : "good",
        note: "Valor de hoje do empreendimento inteiro",
        pills: [partialPill],
      },
      returns && {
        key: "irr",
        label: "TIR",
        value: totals.irrAmbiguous ? "Use o VPL" : annualRate(totals.irrBps),
        tone: "neutral",
        note: irrNote(totals, rateText),
        pills: [Number.isSafeInteger(totals.irrBps)
          ? (totals.irrBps >= data.rate?.bps ? ["Acima da taxa", "good"] : ["Abaixo da taxa", "bad"])
          : null],
      },
      // A meta continua visível com premissas, agora comparada ao estoque na tabela por obra.
      goalTile([]),
      ...liquidityTiles,
    ];
  }

  return <section className="director-dashboard-kpis" aria-label="Indicadores principais">
    {coverage}
    <div className="director-dashboard-kpis__grid">
      {tiles.filter(Boolean).map((tile) => <Kpi key={tile.key} tile={tile} />)}
    </div>
  </section>;
};

export default KpiStrip;
